import { useMemo, useState, useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { AgentSessionEnrichedRow, AgentSessionsRow as AgentSessionRow } from '@/types/appRouter';
import { trpc } from '@/api/trpc';
import { useTrpcMutation } from '@/api/useTrpcMutation';
import { ChatSessionItem } from './ChatSessionItem';
import { AutopilotPlansList } from './AutopilotPlansList';
import Dropdown from '@/components/UI/Dropdown';
import { extractAutopilotPlanId } from './agentSessionContext';

function getAutopilotPlanId(session: AgentSessionRow | AgentSessionEnrichedRow): number | null {
    const autopilotPlanId = 'autopilotPlanId' in session ? session.autopilotPlanId : null;
    return extractAutopilotPlanId({ autopilotPlanId, initialContextJson: session.initialContextJson });
}

interface ChatSessionSidebarProps {
    activeSessionId: number | null;
    isLoading: boolean;
    onNewChat: () => void;
    onNewAutopilot: () => void;
    onSelectPlan: (planId: number) => void;
    onSelectSession: (id: number) => void;
    sessions: AgentSessionEnrichedRow[];
}

export function ChatSessionSidebar({
    activeSessionId,
    isLoading,
    onNewChat,
    onNewAutopilot,
    onSelectPlan,
    onSelectSession,
    sessions
}: ChatSessionSidebarProps) {
    const queryClient = useQueryClient();
    const [searchQuery, setSearchQuery] = useState('');
    const [showActiveOnly, setShowActiveOnly] = useState(false);

    const searchSessionsQuery = useQuery({
        ...trpc.agent.searchSessions.queryOptions({ query: searchQuery, limit: 20 }),
        enabled: searchQuery.length > 2
    });

    const invalidateSessionsList = useCallback(async () => {
        await queryClient.invalidateQueries(trpc.agent.listSessionsEnriched.pathFilter());
    }, [queryClient]);

    const renameMutation = useTrpcMutation(trpc.agent.renameSession.mutationOptions({ onSuccess: invalidateSessionsList }), {
        successMessage: 'Chat renamed',
        fallbackErrorMessage: 'Failed to rename chat'
    });

    const pinMutation = useTrpcMutation(trpc.agent.pinSession.mutationOptions({ onSuccess: invalidateSessionsList }), {
        fallbackErrorMessage: 'Failed to pin chat'
    });

    const unpinMutation = useTrpcMutation(trpc.agent.unpinSession.mutationOptions({ onSuccess: invalidateSessionsList }), {
        fallbackErrorMessage: 'Failed to unpin chat'
    });

    const archiveMutation = useTrpcMutation(trpc.agent.archiveSession.mutationOptions({ onSuccess: invalidateSessionsList }), {
        successMessage: 'Chat archived',
        fallbackErrorMessage: 'Failed to archive chat'
    });

    const regenerateTitleMutation = useTrpcMutation(
        trpc.agent.regenerateSessionTitle.mutationOptions({ onSuccess: invalidateSessionsList }),
        { successMessage: 'Title regenerated', fallbackErrorMessage: 'Failed to regenerate title' }
    );

    const activeRunCount = useMemo(() => sessions.filter((s) => s.activeRunStatus !== null).length, [sessions]);
    const searchedSessions = searchSessionsQuery.data?.sessions ?? null;

    const displaySessions = useMemo<(AgentSessionRow | AgentSessionEnrichedRow)[]>(() => {
        let list: (AgentSessionRow | AgentSessionEnrichedRow)[] = searchQuery.length > 2 && searchedSessions ? searchedSessions : sessions;

        if (searchQuery.length > 0 && searchQuery.length <= 2) {
            const lower = searchQuery.toLowerCase();
            list = sessions.filter((s) => (s.title ?? '').toLowerCase().includes(lower));
        }

        if (showActiveOnly) {
            list = list.filter((s) => 'activeRunStatus' in s && s.activeRunStatus !== null);
        }

        return list;
    }, [searchQuery, searchedSessions, sessions, showActiveOnly]);

    const pinnedSessions = useMemo(() => displaySessions.filter((s) => s.isPinned === 1), [displaySessions]);
    const recentSessions = useMemo(() => displaySessions.filter((s) => s.isPinned !== 1), [displaySessions]);

    const newButtonContent = <span className="text-xs text-white">+ New</span>;

    return (
        <div className="rounded-lg border border-zinc-800 bg-zinc-900/40 overflow-hidden flex flex-col h-[calc(100vh-220px)] min-h-[520px]">
            <div className="flex items-center justify-between px-3 py-2 border-b border-zinc-800 shrink-0">
                <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold text-white">Chats</span>
                    {activeRunCount > 0 && (
                        <button
                            className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${showActiveOnly ? 'bg-green-600 text-white' : 'bg-zinc-700 text-zinc-300 hover:bg-zinc-600'}`}
                            title="Filter to active runs"
                            type="button"
                            onClick={() => setShowActiveOnly(!showActiveOnly)}
                        >
                            {activeRunCount} active
                        </button>
                    )}
                </div>
                <Dropdown
                    hideChevron
                    buttonClassName="!px-2 !py-1 !text-xs bg-blue-600 hover:bg-blue-500 !text-white !rounded-md"
                    buttonContent={newButtonContent}
                    menuClassName="!w-40"
                    position="right"
                >
                    <button
                        className="w-full text-left px-3 py-1.5 text-sm text-zinc-300 hover:bg-zinc-700"
                        type="button"
                        onClick={onNewChat}
                    >
                        New Chat
                    </button>
                    <button
                        className="w-full text-left px-3 py-1.5 text-sm text-fuchsia-300 hover:bg-zinc-700"
                        type="button"
                        onClick={onNewAutopilot}
                    >
                        New Autopilot
                    </button>
                </Dropdown>
            </div>

            <div className="px-3 py-2 border-b border-zinc-800 shrink-0">
                <input
                    aria-label="Search chats"
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-md px-2.5 py-1.5 text-sm text-white placeholder-zinc-500 outline-none focus:border-zinc-500"
                    placeholder="Search chats..."
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                />
            </div>

            <div className="flex-1 overflow-auto">
                {isLoading && <div className="p-3 text-sm text-zinc-400">Loading...</div>}

                {!isLoading && displaySessions.length === 0 && (
                    <div className="p-3 text-sm text-zinc-400">
                        {searchQuery ? 'No matching chats found.' : 'No chats yet. Start a new one!'}
                    </div>
                )}

                {pinnedSessions.length > 0 && (
                    <>
                        <div className="px-3 pt-2 pb-1 text-xs font-medium text-zinc-500 uppercase tracking-wider">Pinned</div>
                        {pinnedSessions.map((s) => (
                            <ChatSessionItem
                                autopilotPlanId={getAutopilotPlanId(s)}
                                isSelected={s.id === activeSessionId}
                                key={s.id}
                                session={s}
                                onArchive={(id) => archiveMutation.mutate({ sessionId: id })}
                                onPin={(id) => pinMutation.mutate({ sessionId: id })}
                                onRegenerateTitle={(id) => regenerateTitleMutation.mutate({ sessionId: id })}
                                onRename={(id, title) => renameMutation.mutate({ sessionId: id, title })}
                                onSelect={onSelectSession}
                                onUnpin={(id) => unpinMutation.mutate({ sessionId: id })}
                            />
                        ))}
                    </>
                )}

                {recentSessions.length > 0 && (
                    <>
                        {pinnedSessions.length > 0 && (
                            <div className="px-3 pt-2 pb-1 text-xs font-medium text-zinc-500 uppercase tracking-wider">Recent</div>
                        )}
                        {recentSessions.map((s) => (
                            <ChatSessionItem
                                autopilotPlanId={getAutopilotPlanId(s)}
                                isSelected={s.id === activeSessionId}
                                key={s.id}
                                session={s}
                                onArchive={(id) => archiveMutation.mutate({ sessionId: id })}
                                onPin={(id) => pinMutation.mutate({ sessionId: id })}
                                onRegenerateTitle={(id) => regenerateTitleMutation.mutate({ sessionId: id })}
                                onRename={(id, title) => renameMutation.mutate({ sessionId: id, title })}
                                onSelect={onSelectSession}
                                onUnpin={(id) => unpinMutation.mutate({ sessionId: id })}
                            />
                        ))}
                    </>
                )}

                {/* Autopilot Plans section */}
                <AutopilotPlansList onNewAutopilot={onNewAutopilot} onSelectPlan={onSelectPlan} />
            </div>
        </div>
    );
}
